// client/src/contexts/OrganizationContext.js

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { fetchOrganization, switchUserOrganization } from '../utils/organizationUtils';

const OrganizationContext = createContext({});

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
};

export const OrganizationProvider = ({ children }) => {
  const { user, userRole, loading: authLoading } = useAuth();
  const [currentOrganization, setCurrentOrganization] = useState(null);
  const [userOrganizations, setUserOrganizations] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch current organization details
  const fetchCurrentOrganization = async (organizationId) => {
    if (!organizationId) return null;
    
    try {
      setLoading(true);
      const org = await fetchOrganization(organizationId);
      setCurrentOrganization(org);
      return org;
    } catch (error) {
      console.error('Error fetching current organization:', error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Fetch all organizations user belongs to
  const fetchUserOrganizations = async (organizationIds) => {
    if (!organizationIds || organizationIds.length === 0) return [];
    
    try {
      const orgs = await Promise.all(
        organizationIds.map(id => fetchOrganization(id))
      );
      const validOrgs = orgs.filter(org => org !== null);
      setUserOrganizations(validOrgs);
      return validOrgs;
    } catch (error) {
      console.error('Error fetching user organizations:', error);
      return [];
    }
  };

  // Switch organization context
  const switchOrganization = async (organizationId) => {
    if (!user) return false;
    
    try {
      setLoading(true);
      await switchUserOrganization(user.uid, organizationId);
      await fetchCurrentOrganization(organizationId);
      
      // Refresh auth context to get updated user data
      // This will trigger a re-fetch of user data with new currentOrganizationId
      window.location.reload(); // Simple approach for now
      
      return true;
    } catch (error) {
      console.error('Error switching organization:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Initialize organization data when user changes
  useEffect(() => {
    const initializeOrganizationData = async () => {
      // Don't initialize until auth is fully loaded and user is authenticated
      if (authLoading || !user || !userRole) {
        setCurrentOrganization(null);
        setUserOrganizations([]);
        return;
      }

      // Fetch user data to get organization info
      try {
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('../firebase');
        
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const currentOrgId = userData.currentOrganizationId;
          const organizationIds = userData.organizationIds || [];
          
          // Fetch current organization
          if (currentOrgId) {
            await fetchCurrentOrganization(currentOrgId);
          }
          
          // Fetch all user organizations
          await fetchUserOrganizations(organizationIds);
        }
      } catch (error) {
        console.error('Error initializing organization data:', error);
      }
    };

    initializeOrganizationData();
  }, [authLoading, user, userRole]);

  const value = {
    currentOrganization,
    userOrganizations,
    loading,
    switchOrganization,
    fetchCurrentOrganization,
    fetchUserOrganizations
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
};
